// Generat automat din README.md - Enhanced cu funcționalități interactive
import React, { useState, useEffect } from 'react';
import { Sparkles, FileText, Image, Video, Share2, Download, Plus, Search, Filter, Eye, Copy, Heart, CheckCircle, X, Info, AlertTriangle, Wand2, Target, Zap, TrendingUp } from 'lucide-react';
import Card from '../../components/Card';
import Table from '../../components/Table';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';

// Toast Component
const Toast = ({ message, type, onClose }) => {
  const icons = {
    success: CheckCircle,
    error: AlertTriangle,
    warning: AlertTriangle,
    info: Info
  };

  const colors = {
    success: 'bg-green-500',
    error: 'bg-red-500', 
    warning: 'bg-yellow-500',
    info: 'bg-blue-500'
  };

  const Icon = icons[type];

  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);

    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`${colors[type]} text-white p-4 rounded-lg shadow-lg flex items-center space-x-3 animate-pulse`}>
      <Icon className="w-5 h-5" />
      <span>{message}</span>
      <button onClick={onClose}>
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

// Toast Hook
const useToast = () => {
  const [toasts, setToasts] = useState([]);

  const showToast = (message, type = 'info') => {
    const id = Date.now();
    const newToast = { id, message, type };
    
    setToasts(prev => [...prev, newToast]);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  return { toasts, showToast, removeToast };
};

// Validation utilities
const validators = {
  required: (value) => {
    if (!value || value.toString().trim() === '') {
      return 'Acest câmp este obligatoriu';
    }
    return null;
  },

  minLength: (min) => (value) => {
    if (value && value.length < min) {
      return `Minimum ${min} caractere`;
    }
    return null;
  },

  maxLength: (max) => (value) => {
    if (value && value.length > max) {
      return `Maximum ${max} caractere`;
    }
    return null;
  }
};

// Validation Hook
const useValidation = (formData, rules) => {
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    
    Object.keys(rules).forEach(field => {
      const fieldRules = rules[field];
      const value = formData[field];
      
      fieldRules.forEach(rule => {
        const error = rule(value);
        if (error && !newErrors[field]) {
          newErrors[field] = error;
        }
      });
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const clearErrors = () => setErrors({});

  return { errors, validate, clearErrors };
};

const GenerareContinutAI = () => {
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showContentModal, setShowContentModal] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  const [selectedContentType, setSelectedContentType] = useState('text');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [isGenerating, setIsGenerating] = useState(false);

  // State pentru conținut generat
  const [generatedContent, setGeneratedContent] = useState([
    {
      id: 1,
      title: 'Email Marketing - Oferta Primăvară',
      type: 'Email',
      category: 'Marketing',
      content: 'Descoperă ofertele noastre exclusive de primăvară! Până la 50% reduceri la toate produsele tech. Doar pentru clienții fideli, timp limitat până pe 30 aprilie. Nu rata această oportunitate unică de a actualiza tehnologia la prețuri de nerefuzat!',
      status: 'Publicat',
      engagement: '8.5%',
      platform: 'Email Campaign',
      createdAt: '2024-03-15 14:30',
      author: 'AI Generator',
      likes: 142,
      shares: 23,
      wordCount: 45,
      tone: 'Persuasiv',
      targetAudience: 'Clienți existenți'
    },
    {
      id: 2,
      title: 'Post Facebook - Lansare Produs Nou',
      type: 'Social Media',
      category: 'Lansare',
      content: '🚀 Lansăm cu mândrie noul nostru produs revoluționar! Tehnologie de ultimă generație, design elegant și performanțe de excepție. Transformă-ți experiența digitală cu inovația care redefinește standardele. #TehnologieNoua #Inovatie #RevolutionTech',
      status: 'Draft',
      engagement: '12.3%',
      platform: 'Facebook',
      createdAt: '2024-03-16 09:15',
      author: 'AI Generator',
      likes: 89,
      shares: 34,
      wordCount: 28,
      tone: 'Entuziast',
      targetAudience: 'Tech enthusiasts'
    },
    {
      id: 3,
      title: 'Newsletter - Actualizări Lunare',
      type: 'Newsletter',
      category: 'Informativ',
      content: 'Salut! În această lună am pregătit pentru tine o selecție specială de noutăți și actualizări. Descoperă cele mai noi funcționalități, află despre planurile noastre de viitor și nu rata ofertele exclusive pentru abonați. Îți mulțumim că ești alături de noi în această călătorie!',
      status: 'Programat',
      engagement: '6.8%',
      platform: 'Email Newsletter',
      createdAt: '2024-03-14 16:45',
      author: 'AI Generator',
      likes: 67,
      shares: 12,
      wordCount: 52,
      tone: 'Prietenos',
      targetAudience: 'Abonați newsletter'
    }
  ]);

  // Form state pentru generare conținut
  const [contentFormData, setContentFormData] = useState({
    title: '',
    type: 'Email',
    category: 'Marketing',
    prompt: '',
    tone: 'Profesional',
    targetAudience: 'General',
    platform: 'Email Campaign',
    maxWords: 100
  });

  // Toast system
  const { toasts, showToast, removeToast } = useToast();

  // Form validation rules
  const contentValidationRules = {
    title: [validators.required, validators.minLength(5), validators.maxLength(100)],
    prompt: [validators.required, validators.minLength(10), validators.maxLength(500)]
  };

  const { errors, validate, clearErrors } = useValidation(contentFormData, contentValidationRules);

  // Calculare statistici dinamice
  const contentStats = {
    totalGenerated: generatedContent.length,
    thisMonth: generatedContent.filter(content => {
      const contentDate = new Date(content.createdAt);
      const thisMonth = new Date();
      return contentDate.getMonth() === thisMonth.getMonth() && 
             contentDate.getFullYear() === thisMonth.getFullYear();
    }).length,
    avgEngagement: generatedContent.length > 0 ? 
      (generatedContent.reduce((sum, content) => sum + parseFloat(content.engagement), 0) / generatedContent.length).toFixed(1) + '%' : 
      '0%',
    totalWords: generatedContent.reduce((sum, content) => sum + content.wordCount, 0)
  };

  // Content types pentru statistici
  const contentTypes = [
    { type: 'Email', name: 'Email Marketing', count: generatedContent.filter(c => c.type === 'Email').length, icon: FileText },
    { type: 'Social Media', name: 'Social Media', count: generatedContent.filter(c => c.type === 'Social Media').length, icon: Share2 },
    { type: 'Newsletter', name: 'Newsletter', count: generatedContent.filter(c => c.type === 'Newsletter').length, icon: Image },
    { type: 'Blog', name: 'Blog Posts', count: generatedContent.filter(c => c.type === 'Blog').length, icon: Video }
  ];

  // AI Templates predefiniți
  const aiTemplates = [
    {
      id: 1,
      name: 'Email Promotional',
      description: 'Template pentru emailuri de promovare cu oferte speciale',
      type: 'Email',
      tone: 'Persuasiv',
      structure: 'Hook + Ofertă + Urgență + CTA',
      useCount: 89
    },
    {
      id: 2,
      name: 'Post Social Media',
      description: 'Format optimizat pentru rețelele sociale cu hashtag-uri',
      type: 'Social Media',
      tone: 'Casual',
      structure: 'Hook + Conținut + Hashtags + CTA',
      useCount: 156
    },
    {
      id: 3,
      name: 'Newsletter Informativ',
      description: 'Newsletter cu actualizări și noutăți pentru abonați',
      type: 'Newsletter',
      tone: 'Prietenos',
      structure: 'Salut + Noutăți + Valoare + Concluzie',
      useCount: 67
    }
  ];

  // Event handlers
  const handleContentClick = (content) => {
    setSelectedContent(content);
    setShowContentModal(true);
  };

  const handleContentFormChange = (field, value) => {
    setContentFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      clearErrors();
    }
  };

  const resetContentForm = () => {
    setContentFormData({
      title: '',
      type: 'Email',
      category: 'Marketing',
      prompt: '',
      tone: 'Profesional',
      targetAudience: 'General',
      platform: 'Email Campaign',
      maxWords: 100
    });
    clearErrors();
  };

  const handleGenerateContent = async () => {
    if (!validate()) {
      showToast('Completează toate câmpurile obligatorii!', 'error');
      return;
    }

    setIsGenerating(true);

    try {
      // Simulate AI generation (2-4 seconds)
      await new Promise(resolve => setTimeout(resolve, 2500));

      // Generate mock AI content based on prompt
      const aiGeneratedContent = generateMockAIContent(contentFormData.prompt, contentFormData.tone, contentFormData.maxWords);

      // Create new content
      const newContent = {
        id: generatedContent.length + 1,
        title: contentFormData.title,
        type: contentFormData.type,
        category: contentFormData.category,
        content: aiGeneratedContent,
        status: 'Draft',
        engagement: (Math.random() * 15 + 5).toFixed(1) + '%',
        platform: contentFormData.platform,
        createdAt: new Date().toISOString().slice(0, 16).replace('T', ' '),
        author: 'AI Generator',
        likes: Math.floor(Math.random() * 200) + 20,
        shares: Math.floor(Math.random() * 50) + 5,
        wordCount: aiGeneratedContent.split(' ').length,
        tone: contentFormData.tone,
        targetAudience: contentFormData.targetAudience
      };

      // Add to content list
      setGeneratedContent(prev => [newContent, ...prev]);

      // Success feedback
      showToast(`Conținut AI generat cu succes: "${newContent.title}"!`, 'success');

      // Reset and close
      resetContentForm();
      setShowGenerateModal(false);

      // Auto-open preview modal
      setTimeout(() => {
        setSelectedContent(newContent);
        setShowContentModal(true);
      }, 500);

    } catch (error) {
      showToast('A apărut o eroare la generarea conținutului!', 'error');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDeleteContent = (contentId) => {
    const content = generatedContent.find(c => c.id === contentId);
    if (window.confirm(`Ești sigur că vrei să ștergi conținutul "${content?.title}"?`)) {
      setGeneratedContent(prev => prev.filter(c => c.id !== contentId));
      showToast('Conținut șters cu succes!', 'success');
      if (selectedContent?.id === contentId) {
        setShowContentModal(false);
      }
    }
  };

  const handleCopyContent = (content) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(content.content);
      showToast('Conținut copiat în clipboard!', 'success');
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = content.content;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      showToast('Conținut copiat în clipboard!', 'success');
    }
  };

  const handleUseTemplate = (template) => {
    setContentFormData(prev => ({
      ...prev,
      type: template.type,
      tone: template.tone,
      title: `${template.name} - ${new Date().toLocaleDateString()}`,
      prompt: `Creează ${template.name.toLowerCase()} folosind structura: ${template.structure}`
    }));
    setShowTemplateModal(false);
    setShowGenerateModal(true);
  };

  // Generate mock AI content
  const generateMockAIContent = (prompt, tone, maxWords) => {
    const toneStyles = {
      'Profesional': ['Vă informăm', 'Cu respect', 'Este important să știți', 'Recomandăm', 'Vă mulțumim pentru încredere'],
      'Casual': ['Salut!', 'Ce zici de', 'Super tare', 'Nu rata', 'Hai să vedem'],
      'Persuasiv': ['Descoperă', 'Nu rata', 'Doar pentru tine', 'Ofertă limitată', 'Acționează acum'],
      'Prietenos': ['Salut prietene!', 'Îți spunem cu drag', 'Ne bucurăm să îți prezentăm', 'Sperăm să îți placă'],
      'Entuziast': ['🚀 Incredibil!', '✨ Amazing!', 'Super știri!', 'Te va uimi', 'Pregătește-te pentru']
    };

    const starters = toneStyles[tone] || toneStyles['Profesional'];
    const starter = starters[Math.floor(Math.random() * starters.length)];
    
    const templates = [
      `${starter} ${prompt.toLowerCase()}. Această oportunitate unică îți oferă șansa de a experimenta ceva cu adevărat special. Nu rata momentul perfect pentru a te alătura comunității noastre și a descoperi beneficiile exclusive.`,
      `${starter} cu privire la ${prompt.toLowerCase()}. Echipa noastră a lucrat din greu pentru a-ți oferi cea mai bună experiență posibilă. Rezultatele vorbesc de la sine și suntem încrezători că vei fi mulțumit.`,
      `${starter} Îți prezentăm ${prompt.toLowerCase()} - soluția pe care o căutai. Cu caracteristici inovatoare și o abordare centrată pe utilizator, acest produs schimbă complet perspectiva asupra industriei.`
    ];

    let content = templates[Math.floor(Math.random() * templates.length)];
    
    // Trim to max words
    const words = content.split(' ');
    if (words.length > maxWords) {
      content = words.slice(0, maxWords).join(' ') + '...';
    }

    return content;
  };

  // Helper functions
  const getStatusColor = (status) => {
    const colors = {
      'Publicat': 'success',
      'Draft': 'warning',
      'Programat': 'info',
      'Arhivat': 'default'
    };
    return colors[status] || 'default';
  };

  const getToneColor = (tone) => {
    const colors = {
      'Profesional': 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
      'Casual': 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
      'Persuasiv': 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
      'Prietenos': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
      'Entuziast': 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400'
    };
    return colors[tone] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
  };

  // Table columns
  const contentColumns = [
    { 
      header: 'Conținut', 
      key: 'title',
      render: (value, row) => (
        <div>
          <p className="font-semibold text-gray-900 dark:text-white">{value}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">{row.type} • {row.wordCount} cuvinte</p>
        </div>
      )
    },
    { header: 'Categorie', key: 'category' },
    { 
      header: 'Ton', 
      key: 'tone',
      render: (value) => (
        <span className={`inline-block px-2 py-1 text-sm font-medium rounded-full ${getToneColor(value)}`}>
          {value}
        </span>
      )
    },
    { 
      header: 'Status', 
      key: 'status',
      render: (value) => <Badge variant={getStatusColor(value)}>{value}</Badge>
    },
    { 
      header: 'Engagement', 
      key: 'engagement',
      render: (value) => (
        <div className="flex items-center space-x-1">
          <TrendingUp className="w-4 h-4 text-green-600" />
          <span className="font-semibold text-green-600">{value}</span>
        </div>
      )
    },
    { header: 'Platform', key: 'platform' },
    { header: 'Data Creare', key: 'createdAt' },
    { 
      header: 'Acțiuni', 
      key: 'actions',
      render: (_, row) => (
        <div className="flex items-center space-x-2">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              handleCopyContent(row);
            }}
            className="text-blue-600 hover:text-blue-800 dark:text-blue-400"
            title="Copiază conținut"
          >
            <Copy className="w-4 h-4" />
          </button>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteContent(row.id);
            }}
            className="text-red-600 hover:text-red-800 dark:text-red-400"
            title="Șterge conținut"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )
    }
  ];

  // Filter data
  const filteredContent = generatedContent.filter(content => {
    const matchesSearch = content.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         content.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         content.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || content.type === filterType;
    return matchesSearch && matchesType;
  });

  // Get unique types for filter
  const contentTypesList = [...new Set(generatedContent.map(c => c.type))];

  return (
    <div className="space-y-6">
      {/* Toast Notifications */}
      <div className="fixed top-4 right-4 space-y-2 z-50">
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Generare Conținut AI</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Creează automat imagini și texte cu template-uri predefinite și AI generativ avansat
          </p>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowTemplateModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Sparkles className="w-4 h-4" />
            <span>Template-uri AI</span>
          </button>
          <button
            onClick={() => setShowGenerateModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Wand2 className="w-4 h-4" />
            <span>Generează Conținut</span>
          </button>
        </div>
      </div>

      {/* Statistici overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <div className="flex items-center space-x-3">
            <Sparkles className="w-8 h-8 text-purple-600" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{contentStats.totalGenerated}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Generat</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center space-x-3">
            <Zap className="w-8 h-8 text-yellow-600" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{contentStats.thisMonth}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Luna Aceasta</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center space-x-3">
            <TrendingUp className="w-8 h-8 text-green-600" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{contentStats.avgEngagement}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Engagement Mediu</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center space-x-3">
            <FileText className="w-8 h-8 text-blue-600" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{(contentStats.totalWords / 1000).toFixed(1)}K</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Cuvinte Generate</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Content types grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {contentTypes.map((type) => {
          const Icon = type.icon;
          return (
            <Card key={type.type}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Icon className="w-6 h-6 text-primary-600" />
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white">{type.name}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{type.count} generate</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-primary-600">{type.count}</p>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Content Management */}
      <Card>
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Conținut Generat</h3>
          <p className="text-gray-600 dark:text-gray-400">Management complet al conținutului generat cu AI, cu preview și analiză performanță</p>
        </div>

        <div className="space-y-6">
          {/* Filtrare și căutare */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="w-5 h-5 absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="Caută conținut, titluri sau categorii..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="all">Toate tipurile</option>
              {contentTypesList.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          
          <Table
            columns={contentColumns}
            data={filteredContent}
            onRowClick={(row) => handleContentClick(row)}
          />
        </div>
      </Card>

      {/* Modal generare conținut */}
      {showGenerateModal && (
        <Modal
          isOpen={showGenerateModal}
          onClose={() => {
            setShowGenerateModal(false);
            resetContentForm();
          }}
          title="Generare Conținut AI"
        >
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-700">
              <div className="flex items-center space-x-2 mb-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                <h4 className="font-semibold text-purple-900 dark:text-purple-100">AI Content Generator</h4>
              </div>
              <p className="text-sm text-purple-700 dark:text-purple-300">
                Folosește AI-ul nostru avansat pentru a crea conținut de marketing personalizat și optimizat pentru audiența ta țintă.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Titlu Conținut *
                </label>
                <input
                  type="text"
                  value={contentFormData.title}
                  onChange={(e) => handleContentFormChange('title', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white ${
                    errors.title ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                  placeholder="Ex: Email promocional pentru Black Friday"
                />
                {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tip Conținut
                </label>
                <select
                  value={contentFormData.type}
                  onChange={(e) => handleContentFormChange('type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="Email">Email Marketing</option>
                  <option value="Social Media">Social Media</option>
                  <option value="Newsletter">Newsletter</option>
                  <option value="Blog">Blog Post</option>
                  <option value="Advertisement">Advertisement</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Prompt pentru AI *
              </label>
              <textarea
                value={contentFormData.prompt}
                onChange={(e) => handleContentFormChange('prompt', e.target.value)}
                rows={4}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white ${
                  errors.prompt ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="Descrie ce vrei să genereze AI-ul. Ex: Crează un email pentru promovarea unei oferte de Black Friday cu reduceri de până la 70% pentru produse tech..."
              />
              {errors.prompt && <p className="text-red-500 text-xs mt-1">{errors.prompt}</p>}
              <p className="text-xs text-gray-500 mt-1">
                {contentFormData.prompt.length}/500 caractere
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Ton de Voce
                </label>
                <select
                  value={contentFormData.tone}
                  onChange={(e) => handleContentFormChange('tone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="Profesional">Profesional</option>
                  <option value="Casual">Casual</option>
                  <option value="Persuasiv">Persuasiv</option>
                  <option value="Prietenos">Prietenos</option>
                  <option value="Entuziast">Entuziast</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Audiența Țintă
                </label>
                <select
                  value={contentFormData.targetAudience}
                  onChange={(e) => handleContentFormChange('targetAudience', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="General">General</option>
                  <option value="Clienți existenți">Clienți existenți</option>
                  <option value="Prospects">Prospects</option>
                  <option value="Tech enthusiasts">Tech enthusiasts</option>
                  <option value="Business professionals">Business professionals</option>
                  <option value="Young adults">Young adults</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Lungime Max (cuvinte)
                </label>
                <select
                  value={contentFormData.maxWords}
                  onChange={(e) => handleContentFormChange('maxWords', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value={50}>50 cuvinte (Scurt)</option>
                  <option value={100}>100 cuvinte (Mediu)</option>
                  <option value={200}>200 cuvinte (Lung)</option>
                  <option value={300}>300 cuvinte (Extins)</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Categorie
                </label>
                <select
                  value={contentFormData.category}
                  onChange={(e) => handleContentFormChange('category', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="Marketing">Marketing</option>
                  <option value="Informativ">Informativ</option>
                  <option value="Lansare">Lansare</option>
                  <option value="Promotional">Promoțional</option>
                  <option value="Educational">Educațional</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Platformă
                </label>
                <select
                  value={contentFormData.platform}
                  onChange={(e) => handleContentFormChange('platform', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="Email Campaign">Email Campaign</option>
                  <option value="Facebook">Facebook</option>
                  <option value="Instagram">Instagram</option>
                  <option value="LinkedIn">LinkedIn</option>
                  <option value="Twitter">Twitter</option>
                  <option value="Website">Website</option>
                  <option value="Blog">Blog</option>
                </select>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 pt-4 border-t dark:border-gray-700">
              <button
                onClick={() => {
                  setShowGenerateModal(false);
                  resetContentForm();
                }}
                className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600 rounded-lg transition-colors"
              >
                Anulează
              </button>
              <button
                onClick={handleGenerateContent}
                disabled={isGenerating}
                className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${
                  isGenerating
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white'
                }`}
              >
                {isGenerating && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                )}
                <Wand2 className="w-4 h-4" />
                <span>{isGenerating ? 'Se generează...' : 'Generează cu AI'}</span>
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal template-uri AI */}
      {showTemplateModal && (
        <Modal
          isOpen={showTemplateModal}
          onClose={() => setShowTemplateModal(false)}
          title="Template-uri AI Predefinite"
        >
          <div className="space-y-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-700">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Selectează un template optimizat pentru a genera conținut de înaltă calitate cu AI.
              </p>
            </div>

            <div className="grid gap-4">
              {aiTemplates.map((template) => (
                <div key={template.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                        {template.name}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        {template.description}
                      </p>
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <span>Tip: {template.type}</span>
                        <span>Ton: {template.tone}</span>
                        <span>Folosit: {template.useCount}x</span>
                      </div>
                      <div className="mt-2 text-xs text-gray-400">
                        Structură: {template.structure}
                      </div>
                    </div>
                    <button
                      onClick={() => handleUseTemplate(template)}
                      className="ml-4 px-3 py-1 bg-purple-600 text-white rounded text-sm hover:bg-purple-700 transition-colors"
                    >
                      Folosește
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Modal>
      )}

      {/* Modal preview conținut */}
      {showContentModal && selectedContent && (
        <Modal
          isOpen={showContentModal}
          onClose={() => setShowContentModal(false)}
          title={`Preview - ${selectedContent.title}`}
        >
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 dark:text-white">Detalii Conținut</h3>
                
                <div className="flex items-center space-x-3">
                  <FileText className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Tip și Categorie</p>
                    <p className="font-medium">{selectedContent.type} • {selectedContent.category}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Target className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Audiența Țintă</p>
                    <p className="font-medium">{selectedContent.targetAudience}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <span className={`inline-block px-2 py-1 text-sm font-medium rounded-full ${getToneColor(selectedContent.tone)}`}>
                    {selectedContent.tone}
                  </span>
                  <Badge variant={getStatusColor(selectedContent.status)}>
                    {selectedContent.status}
                  </Badge>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 dark:text-white">Performanță</h3>
                
                <div className="flex items-center space-x-3">
                  <TrendingUp className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Engagement Rate</p>
                    <p className="font-bold text-lg text-green-600">{selectedContent.engagement}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Heart className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Interacțiuni</p>
                    <p className="font-medium">{selectedContent.likes} likes • {selectedContent.shares} shares</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <FileText className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Statistici</p>
                    <p className="font-medium">{selectedContent.wordCount} cuvinte • {selectedContent.platform}</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Conținut Generat</h3>
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                <p className="text-gray-900 dark:text-white leading-relaxed">
                  {selectedContent.content}
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t dark:border-gray-700">
              <button
                onClick={() => handleDeleteContent(selectedContent.id)}
                className="px-4 py-2 text-red-600 bg-red-100 hover:bg-red-200 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/40 rounded-lg transition-colors"
              >
                Șterge
              </button>
              <button
                onClick={() => setShowContentModal(false)}
                className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600 rounded-lg transition-colors"
              >
                Închide
              </button>
              <button
                onClick={() => handleCopyContent(selectedContent)}
                className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors"
              >
                <Copy className="w-4 h-4 inline mr-2" />
                Copiază Conținut
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default GenerareContinutAI;